import { useMemo, type VoidFunctionComponent } from 'react';

import { Status } from '../../../../../../../shared/builds/types';
import { icons, Menu } from '../../../../../generic';
import Button from '../../../../../generic/Button';
import type { Dependency, DependencyForAPI, DependencyState } from '../../../../../../../shared/dependenciesV2';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../../../typings/api';
import type { DependencyToRemove } from '../../../modals/RemoveDependency';

import { AnalyzedMenu, BuildDropdown, ErrorDropdown } from './ActionMenus';

import styles from '../DependencyRow.module.less';

type Props = {
  dependency: Dependency | DependencyForAPI;
  status: Status;
  setDependencyToRemove: (depedency: DependencyToRemove | null) => void;
  setDependencyLocatorToOverwrite: (dependency: string | null) => void;
  setDependencyLocatorToEdit?: (dependency: string | null) => void;
  setManualDependencyAddMode: (mode: ManuallyAddedDependencyRequestKind | null) => void;
  setInProgressBuildId: (buildId: number | null) => void;
  isProjectScope: boolean;
};

export type MenuProps = Omit<Props, 'status' | 'setInProgressBuildId' | 'dependency' > & {
  locator: string;
  revision: DependencyToRemove;
};

export type ErrorMenuProps = MenuProps & {
  dependencyState: DependencyState;
};

export type BuildMenuProps = MenuProps & {
  setInProgressBuildId: (buildId: number | null) => void;
};

const DependencyActionDropdown: VoidFunctionComponent<Props> = ({
  dependency,
  status,
  setDependencyToRemove,
  setDependencyLocatorToOverwrite,
  setDependencyLocatorToEdit,
  setManualDependencyAddMode,
  setInProgressBuildId,
  isProjectScope,
}) => {
  const revisionToRemove = useMemo<DependencyToRemove>(() => ({
    locator: dependency.locator,
    project: { title: dependency.title },
    manual: dependency.isManual,
  }), [dependency]);

  return (
    <div className={styles.DependencyRowDropdown}>
      <Menu
        gap={4}
        direction="left"
        position="initial"
        menuButton={<Button type="outline" icon={icons.ellipsisHorizontal} onClick={(e) => e.stopPropagation()} />}
      >
        {status === Status.Analyzed && (
          <AnalyzedMenu
            locator={dependency.locator}
            revision={revisionToRemove}
            setDependencyToRemove={setDependencyToRemove}
            setManualDependencyAddMode={setManualDependencyAddMode}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
            setDependencyLocatorToEdit={setDependencyLocatorToEdit}
            isProjectScope={isProjectScope}
          />
        )}
        {(status === Status.Failed || status === Status.Unknown) && (
          <ErrorDropdown
            locator={dependency.locator}
            revision={revisionToRemove}
            dependencyState={dependency.status}
            setDependencyToRemove={setDependencyToRemove}
            setManualDependencyAddMode={setManualDependencyAddMode}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
            isProjectScope={isProjectScope}
          />
        )}
        {status === Status.InProgress && (
          <BuildDropdown
            locator={dependency.locator}
            revision={revisionToRemove}
            setDependencyToRemove={setDependencyToRemove}
            setManualDependencyAddMode={setManualDependencyAddMode}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
            setInProgressBuildId={setInProgressBuildId}
            isProjectScope={isProjectScope}
          />
        )}
      </Menu>
    </div>
  );
};

export default DependencyActionDropdown;
